// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
// --- Hooks & Actions
import { useDealDROChangesActions } from 'datastage/store/dealDROChanges/useDealDROChanges';
import { useDealDROChangesData } from '../../hooks/useDealDROChangesData';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Utils
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { cleanDroChangeFilter } from './utils/droChangeSearchUtils';
// --- Styles
import './styles.scss';

/**
 * DealDROChanges Search Form
 *
 * Features:
 * - Renders a filter form for Deal DRO Changes (Start Date, End Date)
 * - Debounced auto-search on input change (400ms delay)
 * - Date range validation (start date must be before end date)
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function DealDROChangesSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const { fetchDealDROChanges, updateDealDROChanges, rangeParam, sortParam, itemsPerPage } =
        useDealDROChangesData();
    const { updateDealDROChangesSearchForm, clearDealDROChanges } = useDealDROChangesActions();
    const { filterForm } = useSelector((s) => s.dealDROChanges);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        dateRange: undefined,
    });

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateDealDROChanges({
                    itemsPerPage: newPageSize,
                    rangeParam: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateDealDROChangesSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    /**
     * Builds filter form data from URL search params.
     */
    function buildFilterFromParams(params) {
        const filterData = {};

        if (params.timestampStart) filterData.timestampStart = params.timestampStart;
        if (params.timestampEnd) filterData.timestampEnd = params.timestampEnd;

        return filterData;
    }

    /**
     * Fetches Deal DRO Changes data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include all fields
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchDealDROChanges(range, mergedFilter);
        if (data) {
            updateDealDROChanges({
                dealDROChanges: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                rangeParam: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearDealDROChanges();
        setValidationErrors({ dateRange: undefined });
        navigate(location.pathname);
    };

    /**
     * Validate that start date is before end date.
     */
    function validateDates(startDate, endDate) {
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            return 'Start Date must be before End Date';
        }
        return undefined;
    }

    /**
     * Handles input changes with validation and debounced search.
     * Validates date range on change.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = { ...validationErrors };

        // Validate date range
        if (field === 'timestampStart') {
            newValidationErrors.dateRange = validateDates(value, filterForm.timestampEnd);
        } else if (field === 'timestampEnd') {
            newValidationErrors.dateRange = validateDates(filterForm.timestampStart, value);
        }

        setValidationErrors(newValidationErrors);

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateDealDROChangesSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateDealDROChanges({ rangeParam: firstPageRange, currentPage: 0 });
                const filtered = cleanDroChangeFilter(newFilterForm, firstPageRange, sortParam);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="deal-dro-changes-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Start Date">
                    <Input
                        labelText="Date Input"
                        type="date"
                        value={filterForm.timestampStart}
                        onChange={(e) => handleInputChange('timestampStart', e.target.value)}
                        className={validationErrors.dateRange ? 'input-error' : ''}
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="End Date">
                    <Input
                        labelText="Date Input"
                        type="date"
                        value={filterForm.timestampEnd}
                        onChange={(e) => handleInputChange('timestampEnd', e.target.value)}
                        className={validationErrors.dateRange ? 'input-error' : ''}
                    />
                    {validationErrors.dateRange && (
                        <div className="validation-error">{validationErrors.dateRange}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
